import { Formik, Form, Field } from "formik";
import React, { useState } from "react";
import { useDispatch } from "react-redux";

import FieldErrorMessage from "@/Components/common/FieldErrorMessage";
import { RequiredSymbol } from "@/Components/common/RequiredSymbol";
import { EyeIcon } from "@/Components/Icons/EyeIcon";
import { EyeSlashIcon } from "@/Components/Icons/EyeSlashIcon";
import { updateNewUser, updateShowBoxes } from "@/reducers/auth";
import { handleAPICatch, showSuccessToast } from "@/utilities/utils";
import { validatePassword } from "@/validations/auth/login";

import { _set_new_password } from "../../../services/auth";
import AuthButton from "../AuthButton";
import LoginFormTopContent from "../LoginFormTopContent";
/**
 * Renders a SetNewPasswordForm component.
 *
 * @param {Object} props - The properties for the component.
 * @return {JSX.Element} A React component representing the SetNewPasswordForm.
 */
const SetNewPasswordForm = ({ action }) => {
  const dispatch = useDispatch();
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConirmPassword, setShowConfirmPassword] = useState(false);

  return (
    <div className="auth_form_wrapper">
      <Formik
        initialValues={{
          new_password: "",
          confirm_password: "",
          password_action: action,
        }}
        validateOnChange={false}
        validateOnBlur={false}
        validateOnMount={false}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(true);
          let response = _set_new_password(values);
          response
            .then((res) => {
              let {
                status,
                data: { message },
              } = res;
              if (status == 200) {
                dispatch(updateShowBoxes("login"));
                dispatch(
                  updateNewUser({
                    id: null,
                    type: null,
                    email: null,
                  }),
                );
                showSuccessToast(message);
              } else {
                showSuccessToast(message);
              }
            })
            .catch((error) => {
              handleAPICatch(error);
            })
            .finally(() => {
              setSubmitting(false);
            });
        }}
      >
        {({
          submitForm,
          isSubmitting,
          errors,
          touched,
          isValid,
          values,
          validateField,
          validateForm,
          setFieldValue,
        }) => (
          <Form className="flex flex-col gap-6">
            <LoginFormTopContent
              title="Set New Password"
              desc="You can now set your AppLocum password here"
            />
            <div>
              <div className="grid gap-4">
                <div className="flex flex-col gap-3">
                  <label htmlFor="new_password" className="label">
                    Enter New Password <RequiredSymbol />
                  </label>
                  <div>
                    <div className="relative">
                      <Field
                        type={showNewPassword ? "text" : "password"}
                        className={`h-[40px] w-full rounded-lg border border-bodyText bg-none text-black shadow-none placeholder:text-[14px] placeholder:font-medium placeholder:text-txtColor autofill:bg-none autofill:shadow-none focus:border focus:border-primary focus:shadow-none focus:ring-0 ${
                          errors.new_password && touched.new_password
                            ? "!border-red-500"
                            : ""
                        }`}
                        autoComplete="off"
                        name="new_password"
                        placeholder="New Password"
                        validate={(value) =>
                          validatePassword(value, {
                            min: 8,
                            max: 20,
                            upperCase: true,
                            lowerCase: true,
                            number: true,
                            special: true,
                          })
                        }
                        onKeyUp={(e) => {
                          if (touched.new_password && errors.new_password) {
                            validateForm();
                          }
                          if (e.keyCode === 13) {
                            submitForm();
                            if (document.activeElement != document.body)
                              document.activeElement.blur();
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="absolute right-[10px] top-1/2 h-[16px] w-[16px] -translate-y-1/2 border-none bg-none text-black"
                        onClick={() => {
                          setShowNewPassword(!showNewPassword);
                        }}
                      >
                        {showNewPassword ? <EyeIcon /> : <EyeSlashIcon />}
                      </button>
                    </div>
                    <FieldErrorMessage name="new_password" />
                  </div>
                </div>
                <div>
                  <div className="relative">
                    <Field
                      type={showConirmPassword ? "text" : "password"}
                      className={`h-[40px] w-full rounded-lg border border-bodyText bg-none text-black shadow-none placeholder:text-[14px] placeholder:font-medium placeholder:text-txtColor autofill:bg-none autofill:shadow-none focus:border focus:border-primary focus:shadow-none focus:ring-0 ${
                        errors.confirm_password && touched.confirm_password
                          ? "!border-red-500"
                          : ""
                      }`}
                      autoComplete="off"
                      name="confirm_password"
                      placeholder="Confirm Password"
                      // validate={(value) =>
                      //   matchPassword(value, values.new_password)
                      // }

                      validate={(value) => {
                        if (!value) {
                          return "Confirm Password Required";
                        } else if (value !== values.new_password) {
                          return `Password doesn't match`;
                        }
                      }}
                      onChange={(e) => {
                        setFieldValue("confirm_password", e.target.value);
                        setTimeout(() => {
                          validateField("confirm_password");
                        }, 50);
                      }}
                      onKeyUp={(e) => {
                        if (
                          touched.confirm_password &&
                          errors.confirm_password
                        ) {
                          validateForm();
                        }
                        if (e.keyCode === 13) {
                          submitForm();
                          if (document.activeElement != document.body)
                            document.activeElement.blur();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="absolute right-[10px] top-1/2 h-[16px] w-[16px] -translate-y-1/2 border-none bg-none text-black"
                      onClick={() => {
                        setShowConfirmPassword(!showConirmPassword);
                      }}
                    >
                      {showConirmPassword ? <EyeIcon /> : <EyeSlashIcon />}
                    </button>
                  </div>
                  <FieldErrorMessage name="confirm_password" />
                </div>
              </div>
              <AuthButton
                submitForm={submitForm}
                isSubmitting={isSubmitting}
                isValid={isValid}
                btnText={"Set New Password"}
                btnLoadingText={"Setting New Password"}
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default SetNewPasswordForm;
